import { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthReady } from '@/hooks/useAuthReady';
import { getIsArchitect } from '@/utils/isArchitectCache';
import { resolvePreferredInstanceId } from '@/utils/instancePreference';

export interface AvailableInstance {
  id: string;
  name: string;
  role: string;
  slug: string;
  tier: string;
  is_active: boolean;
}

export interface NotesWorkspaceReceipt {
  accountId: string | null;
  generation: number;
  status: 'pending' | 'ready' | 'error';
  instanceIds: string[];
}

interface InstanceContextValue {
  currentInstanceId: string | null;
  currentInstanceName: string | null;
  availableInstances: AvailableInstance[];
  instancesLoaded: boolean;
  notesWorkspace: NotesWorkspaceReceipt;
  retryNotesWorkspace: () => Promise<string | null>;
  enterInstance: (id: string, name: string) => void;
  switchInstance: (id: string) => void;
  exitInstance: () => void;
  /**
   * Re-reads memberships + persisted preference for the account signed in at
   * call time, and resolves with the workspace id actually selected (null when
   * signed out, superseded by a newer load, or the read failed).
   */
  refreshInstances: () => Promise<string | null>;

}

const STORAGE_KEY = 'workitect-last-instance-id';

const InstanceContext = createContext<InstanceContextValue>({
  currentInstanceId: null,
  currentInstanceName: null,
  availableInstances: [],
  instancesLoaded: false,
  notesWorkspace: { accountId: null, generation: 0, status: 'pending', instanceIds: [] },
  retryNotesWorkspace: async () => null,
  enterInstance: () => {},
  switchInstance: () => {},
  exitInstance: () => {},
  refreshInstances: async () => null,
});

export function InstanceProvider({ children }: { children: ReactNode }) {
  const { user, isReady, isLinking } = useAuthReady();

  const [currentInstanceId, setCurrentInstanceId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY) || null;
  });
  const [currentInstanceName, setCurrentInstanceName] = useState<string | null>(null);
  const [availableInstances, setAvailableInstances] = useState<AvailableInstance[]>([]);
  const [instancesLoaded, setInstancesLoaded] = useState(false);
  const loadVersionRef = useRef(0);
  const authKey = JSON.stringify([user?.id ?? null, isReady, isLinking]);
  const [notesReceipt, setNotesReceipt] = useState<NotesWorkspaceReceipt & { authKey: string }>({ accountId: null, generation: 0, status: 'pending', instanceIds: [], authKey: '' });
  const notesAttemptRef = useRef(0);
  const committedScope = useRef({ authKey, instanceId: currentInstanceId });
  useLayoutEffect(() => {
    committedScope.current = { authKey, instanceId: currentInstanceId };
    notesAttemptRef.current++;
  }, [authKey, currentInstanceId]);

  const persistLastActiveInstance = useCallback(async (userId: string | null, instanceId: string | null) => {
    if (!userId) return;
    const { error } = await (supabase as any)
      .from('user_preferences')
      .upsert(
        { user_id: userId, last_active_instance_id: instanceId },
        { onConflict: 'user_id' }
      );
    if (error) {
      console.error('[InstanceContext] Failed to persist last active instance:', error);
    }
  }, []);

  const enterInstance = useCallback((id: string, name: string) => {
    setCurrentInstanceId(id);
    setCurrentInstanceName(name);
    localStorage.setItem(STORAGE_KEY, id);
    void persistLastActiveInstance(user?.id ?? null, id);
  }, [user?.id, persistLastActiveInstance]);

  const switchInstance = useCallback((id: string) => {
    const inst = availableInstances.find(i => i.id === id);
    if (inst) {
      setCurrentInstanceId(inst.id);
      setCurrentInstanceName(inst.name);
      localStorage.setItem(STORAGE_KEY, inst.id);
      void persistLastActiveInstance(user?.id ?? null, inst.id);
    }
  }, [user?.id, availableInstances, persistLastActiveInstance]);

  const exitInstance = useCallback(() => {
    setCurrentInstanceId(null);
    setCurrentInstanceName(null);
    localStorage.removeItem(STORAGE_KEY);
    void persistLastActiveInstance(user?.id ?? null, null);
  }, [user?.id, persistLastActiveInstance]);

  const loadAvailableInstances = useCallback(async (userId: string): Promise<{ instances: AvailableInstance[] | null; confirmed: boolean }> => {
    let confirmed = true;
    try {
      // Dedupe is_architect via shared cache; the memberships query still runs
      // in parallel independently.
      const [isArch, membershipsResult] = await Promise.all([
        getIsArchitect(userId).catch(() => { confirmed = false; return false; }),
        supabase.from('instance_members').select('instance_id, role').eq('user_id', userId).eq('is_active', true),
      ]);
      if (membershipsResult.error) confirmed = false;
      const memberships = membershipsResult.data;

      if (isArch === true) {
        const { data: allInstances, error: instancesError } = await supabase
          .from('instances')
          .select('id, name, slug, tier, is_active')
          .eq('is_active', true)
          .order('name');

        if (instancesError) confirmed = false;
        return { confirmed, instances: (allInstances || []).map((inst: any) => ({
          id: inst.id, name: inst.name, slug: inst.slug, tier: inst.tier,
          is_active: inst.is_active, role: 'architect',
        })) };
      }

      if (!memberships || memberships.length === 0) return { instances: [], confirmed };

      const instanceIds = memberships.map(m => m.instance_id);
      const { data: instanceData, error: instanceError } = await (supabase as any)
        .from('instances_member_safe')
        .select('id, name, slug, tier, is_active')
        .in('id', instanceIds)
        .eq('is_active', true);

      if (instanceError) confirmed = false;
      const roleMap = new Map(memberships.map(m => [m.instance_id, m.role]));
      return { confirmed, instances: (instanceData || []).map((inst: any) => ({
        id: inst.id, name: inst.name, slug: inst.slug, tier: inst.tier,
        is_active: inst.is_active, role: roleMap.get(inst.id) || 'user',
      })) };
    } catch (err) {
      console.error('[InstanceContext] Failed to load instances:', err);
      return { instances: null, confirmed: false };
    }
  }, []);

  const applyInstanceSelection = useCallback((instances: AvailableInstance[], dbPreferredInstanceId?: string | null) => {
    setAvailableInstances(instances);

    if (!instances.length) {
      setCurrentInstanceId(null);
      setCurrentInstanceName(null);
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const lastId = localStorage.getItem(STORAGE_KEY);
    const targetId = resolvePreferredInstanceId(instances, dbPreferredInstanceId, lastId);
    const target = instances.find(i => i.id === targetId) ?? instances[0];

    setCurrentInstanceId(target.id);
    setCurrentInstanceName(target.name);
    localStorage.setItem(STORAGE_KEY, target.id);
    return target.id;
  }, []);

  const syncInstancesForUser = useCallback(async (userId: string | null): Promise<string | null> => {
    const startedAuthKey = committedScope.current.authKey;
    const loadVersion = ++loadVersionRef.current;
    const publishNotes = (status: NotesWorkspaceReceipt['status'], instanceIds: string[] = []) => {
      setNotesReceipt({ accountId: userId, generation: ++notesAttemptRef.current, authKey: startedAuthKey, status, instanceIds });
    };
    publishNotes('pending');
    if (!userId) {
      setAvailableInstances([]);
      setCurrentInstanceId(null);
      setCurrentInstanceName(null);
      setInstancesLoaded(true);
      return null;
    }
    setInstancesLoaded(false);
    try {
      const [observation, prefResult] = await Promise.all([
        loadAvailableInstances(userId),
        (supabase as any).from('user_preferences').select('last_active_instance_id').eq('user_id', userId).maybeSingle(),
      ]);
      if (loadVersion !== loadVersionRef.current || startedAuthKey !== committedScope.current.authKey) return null;
      const { instances, confirmed } = observation;
      // Preserve normal selection/fallback semantics. Degradation is tracked separately for Notes.
      if (!instances) {
        setInstancesLoaded(true);
        publishNotes('error');
        return null;
      }
      const preferredFromDb = prefResult.data?.last_active_instance_id ?? null;
      const selectedId = applyInstanceSelection(instances, preferredFromDb);
      if ((selectedId ?? null) !== preferredFromDb) void persistLastActiveInstance(userId, selectedId ?? null);
      setInstancesLoaded(true);
      publishNotes(confirmed && !prefResult.error ? 'ready' : 'error', confirmed && !prefResult.error ? instances.map(i => i.id) : []);
      return selectedId ?? null;
    } catch {
      if (loadVersion !== loadVersionRef.current || startedAuthKey !== committedScope.current.authKey) return null;
      // A rejected preference read historically leaves the shared selection/loading untouched.
      // Report it to Notes without inventing a completed shared reconciliation.
      publishNotes('error');
      return null;
    }
  }, [applyInstanceSelection, loadAvailableInstances, persistLastActiveInstance]);

  const refreshInstances = useCallback(async (): Promise<string | null> => {
    const subject = user?.id ?? null;
    if (!subject) return null;
    return await syncInstancesForUser(subject);
  }, [user?.id, syncInstancesForUser]);


  // Gate on useAuthReady — only load instances when auth is settled and not linking
  useEffect(() => {
    if (isReady && !isLinking) void syncInstancesForUser(user?.id ?? null);
    // Invalidate even if the next account is still linking, and on unmount.
    return () => { loadVersionRef.current++; notesAttemptRef.current++; };

  }, [isReady, isLinking, user?.id, syncInstancesForUser]);

  const retryNotesWorkspace = useCallback(async () => {
    const selected = committedScope.current;
    if (!isReady || isLinking || !user?.id || authKey !== selected.authKey) return null;
    const attempt = ++notesAttemptRef.current;
    const normalLoad = loadVersionRef.current;
    const publish = (status: NotesWorkspaceReceipt['status'], instanceIds: string[] = []) => {
      setNotesReceipt({ accountId: user.id, generation: attempt, authKey, status, instanceIds });
    };
    if (!selected.instanceId) { publish('error'); return null; }
    publish('pending');
    const { instances, confirmed } = await loadAvailableInstances(user.id);
    if (attempt !== notesAttemptRef.current || normalLoad !== loadVersionRef.current ||
        authKey !== committedScope.current.authKey || selected.instanceId !== committedScope.current.instanceId) return null;
    // Verification only: no selection, shared loading/list, localStorage or preference writes.
    const valid = confirmed && !!instances?.some(i => i.id === selected.instanceId);
    publish(valid ? 'ready' : 'error', valid ? [selected.instanceId] : []);
    return valid ? selected.instanceId : null;
  }, [isReady, isLinking, user?.id, authKey, loadAvailableInstances]);
  const notesWorkspace: NotesWorkspaceReceipt = isReady && !isLinking && user?.id &&
    notesReceipt.accountId === user.id && notesReceipt.authKey === authKey
    ? notesReceipt
    : { accountId: user?.id ?? null, generation: notesReceipt.generation, status: 'pending', instanceIds: [] };

  // Allow Task Bot (or any chat surface) to switch instances via custom event.
  useEffect(() => {
    const handler = (e: Event) => {
      const { instanceId } = (e as CustomEvent).detail as { instanceId?: string };
      if (instanceId) switchInstance(instanceId);
    };
    window.addEventListener('workitect-switch-instance', handler);
    return () => window.removeEventListener('workitect-switch-instance', handler);
  }, [switchInstance]);

  return (
    <InstanceContext.Provider value={{
      currentInstanceId, currentInstanceName, availableInstances, instancesLoaded,
      enterInstance, switchInstance, exitInstance, refreshInstances, notesWorkspace, retryNotesWorkspace,
    }}>
      {children}
    </InstanceContext.Provider>
  );
}

export function useInstance() {
  return useContext(InstanceContext);
}
